import { useEffect, useState } from "react";
import axios from "axios";
import { Receipt, Plus, Trash, CheckCircle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { client_id: "", form_type: "24Q", quarter: "Q1", financial_year: "2026-27", tan: "", total_tds_deducted: 0, total_tds_deposited: 0, challan_count: 0, deductee_count: 0, status: "pending", notes: "" };
const FY = ["2026-27", "2025-26", "2024-25"];

export default function ITTDSReturns() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchAll = async () => {
    try {
      const [t, c] = await Promise.all([axios.get(`${API}/it/tds-returns`), axios.get(`${API}/it/clients`)]);
      setRows(t.data); setClients(c.data);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { fetchAll(); }, []);

  const save = async () => {
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/it/tds-returns`, form);
      setOpen(false); setForm(EMPTY); fetchAll();
    } catch (e) { setError(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const markFiled = async (r) => {
    await axios.put(`${API}/it/tds-returns/${r.id}`, { ...r, status: r.status === "filed" ? "pending" : "filed" });
    fetchAll();
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.form_type} ${r.quarter} for ${r.client_name}?`)) return;
    await axios.delete(`${API}/it/tds-returns/${r.id}`); fetchAll();
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="p-4 sm:p-8" data-testid="tds-returns-page">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Receipt size={30} weight="duotone" className="text-emerald-800" />
        <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>TDS Returns (24Q / 26Q)</h1>
        <Button onClick={() => { setForm(EMPTY); setError(""); setOpen(true); }} data-testid="tds-add-btn" className="ml-auto bg-emerald-800 hover:bg-emerald-900 text-white">
          <Plus size={16} className="mr-1" /> New TDS Return
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto sticky-table">
        <table className="w-full text-sm">
          <thead className="sticky-thead bg-emerald-900 text-white uppercase text-[11px] font-bold">
            <tr>
              <th className="px-4 py-3 text-left">Client</th>
              <th className="px-4 py-3 text-left">Form</th>
              <th className="px-4 py-3 text-left">Quarter / FY</th>
              <th className="px-4 py-3 text-left">TAN</th>
              <th className="px-4 py-3 text-right">TDS Deducted</th>
              <th className="px-4 py-3 text-right">Deposited</th>
              <th className="px-4 py-3 text-left">Due Date</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No TDS returns — add one via "New TDS Return"</td></tr>}
            {rows.map((r) => {
              const overdue = r.status !== "filed" && r.due_date < today;
              return (
                <tr key={r.id} className={`border-b border-slate-100 ${overdue ? "bg-red-50" : ""}`} data-testid={`tds-row-${r.id}`}>
                  <td className="px-4 py-3 font-semibold">{r.client_name}<div className="text-[10px] font-mono text-slate-400">{r.client_pan}</div></td>
                  <td className="px-4 py-3"><span className="text-[10px] font-bold bg-slate-100 px-2 py-1 rounded-sm">{r.form_type}</span></td>
                  <td className="px-4 py-3 text-xs">{r.quarter} · FY {r.financial_year}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.tan || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">₹{(r.total_tds_deducted || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">₹{(r.total_tds_deposited || 0).toLocaleString("en-IN")}</td>
                  <td className={`px-4 py-3 text-xs font-bold ${overdue ? "text-red-700" : "text-slate-600"}`}>{r.due_date}{overdue && " ⚠"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-sm uppercase ${r.status === "filed" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                      {r.status}{r.filed_on ? ` (${r.filed_on})` : ""}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => markFiled(r)} className={`mr-3 ${r.status === "filed" ? "text-slate-400" : "text-emerald-700"} hover:opacity-70`} title={r.status === "filed" ? "Mark pending" : "Mark filed"} data-testid={`tds-toggle-${r.id}`}>
                      <CheckCircle size={18} weight={r.status === "filed" ? "fill" : "regular"} />
                    </button>
                    <button onClick={() => remove(r)} className="text-red-400 hover:text-red-700" data-testid={`tds-del-${r.id}`}><Trash size={16} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New TDS Return</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Client (Deductor) *</Label>
              <select data-testid="tds-client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm">
                <option value="">— Select —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.pan})</option>)}
              </select>
            </div>
            <div><Label>Form Type</Label>
              <select data-testid="tds-form-type" value={form.form_type} onChange={(e) => setForm({ ...form, form_type: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm">
                <option value="24Q">24Q — Salary TDS</option><option value="26Q">26Q — Non-Salary TDS</option>
              </select></div>
            <div><Label>Quarter</Label>
              <select data-testid="tds-quarter" value={form.quarter} onChange={(e) => setForm({ ...form, quarter: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm">
                {["Q1", "Q2", "Q3", "Q4"].map((q) => <option key={q}>{q}</option>)}
              </select></div>
            <div><Label>Financial Year</Label>
              <select data-testid="tds-fy" value={form.financial_year} onChange={(e) => setForm({ ...form, financial_year: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm">
                {FY.map((f) => <option key={f}>{f}</option>)}
              </select></div>
            <div><Label>TAN</Label><Input data-testid="tds-tan" value={form.tan} onChange={(e) => setForm({ ...form, tan: e.target.value.toUpperCase() })} placeholder="ABCD12345E" /></div>
            <div><Label>TDS Deducted (₹)</Label><Input type="number" data-testid="tds-deducted" value={form.total_tds_deducted} onChange={(e) => setForm({ ...form, total_tds_deducted: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>TDS Deposited (₹)</Label><Input type="number" data-testid="tds-deposited" value={form.total_tds_deposited} onChange={(e) => setForm({ ...form, total_tds_deposited: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>Challans</Label><Input type="number" data-testid="tds-challans" value={form.challan_count} onChange={(e) => setForm({ ...form, challan_count: parseInt(e.target.value) || 0 })} /></div>
            <div><Label>Deductees</Label><Input type="number" data-testid="tds-deductees" value={form.deductee_count} onChange={(e) => setForm({ ...form, deductee_count: parseInt(e.target.value) || 0 })} /></div>
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded-sm" data-testid="tds-error">{error}</div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy || !form.client_id} data-testid="tds-save-btn" className="bg-emerald-800 hover:bg-emerald-900 text-white">{busy ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
